import { useState } from "react";
import { Box, Button, InputAdornment, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCRUD } from "../../api/crud";
import { ERROR_SNACKBAR, JOB_SERVICES_ENDPOINT, SUCCESS_SNACKBAR } from "../../consts";
import {
  DECIMAL_FOUR_PLACES_INPUT_REGEX,
  formatDecimalForDisplay,
  formatDecimalForInput,
  formatMoney,
  toApiDecimal,
} from "../../utils";

const extractErrorMessage = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  if (typeof value === "object" && "message" in value) {
    return (value as { message?: string }).message ?? null;
  }
  return null;
};

type AmountField = "price" | "cost";

interface JobServicePriceChangeFormProps {
  serviceId: number | string;
  /** The catalogue price the row shows today; empty when the service has none. */
  price: string | null;
  /** Its internal cost for profitability; empty means the price stands in. */
  cost: string | null;
  onSuccess?: () => void;
}

/**
 * Sets a service's net catalogue price and its internal cost. Tasks keep the
 * figures a service was added at, so this only reaches services added to jobs
 * from now on.
 */
export default function JobServicePriceChangeForm({ serviceId, price, cost, onSuccess }: JobServicePriceChangeFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { update, loading } = useCRUD(JOB_SERVICES_ENDPOINT);
  // Start from today's figures, so a small change is a small edit.
  const [values, setValues] = useState<Record<AmountField, string>>(() => ({
    price: formatDecimalForInput(price),
    cost: formatDecimalForInput(cost),
  }));
  const [focusedField, setFocusedField] = useState<AmountField | null>(null);
  const [errors, setErrors] = useState<Record<AmountField, string | null>>({ price: null, cost: null });

  const handleChange = (field: AmountField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip any grouping first: a value pasted from a grouped display
    // ("1 234,56") would otherwise fail the plain-decimal check.
    const nextValue = e.target.value.replace(/[\s  ]/g, "");
    // A cost below zero means nothing (the backend refuses it too).
    if (field === "cost" && nextValue.startsWith("-")) return;
    if (nextValue === "" || DECIMAL_FOUR_PLACES_INPUT_REGEX.test(nextValue)) {
      setValues((prev) => ({ ...prev, [field]: nextValue }));
    }
  };

  const handleSubmit = async () => {
    setErrors({ price: null, cost: null });
    // An empty field clears the figure, as in the full service form.
    const result = await update({
      id: serviceId,
      price: values.price ? toApiDecimal(values.price) : null,
      cost: values.cost ? toApiDecimal(values.cost) : null,
    });

    if (result?.success) {
      enqueueSnackbar(t("Service price and cost saved."), SUCCESS_SNACKBAR);
      onSuccess?.();
      return;
    }

    setErrors({
      price: extractErrorMessage(result?.errorData?.price),
      cost: extractErrorMessage(result?.errorData?.cost),
    });
    enqueueSnackbar(
      extractErrorMessage(result?.errorData?.message) ||
        extractErrorMessage(result?.errorData?.detail) ||
        t("There was an error, please review the form."),
      ERROR_SNACKBAR
    );
  };

  const amountField = (field: AmountField, label: string, helperText: string, autoFocus = false) => (
    <TextField
      size="small"
      label={label}
      value={focusedField === field ? values[field] : formatDecimalForDisplay(values[field])}
      onChange={handleChange(field)}
      onFocus={() => setFocusedField(field)}
      onBlur={() => setFocusedField(null)}
      inputProps={{ inputMode: "decimal" }}
      InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
      fullWidth
      autoFocus={autoFocus}
      error={Boolean(errors[field])}
      helperText={errors[field] || helperText}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t("Current Price")}
          </Typography>
          {price ? (
            <Typography variant="body1">{`${formatMoney(price)} ${t("Net")}`}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("Not set")}
            </Typography>
          )}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t("Internal cost (net)")}
          </Typography>
          {cost ? (
            <Typography variant="body1">{`${formatMoney(cost)} ${t("Net")}`}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("As the price")}
            </Typography>
          )}
        </Box>
      </Box>

      {amountField(
        "price",
        t("New price net"),
        t("Services already added to jobs keep the price they were added at."),
        true
      )}
      {amountField(
        "cost",
        t("Internal cost (net)"),
        t("What performing it once costs you. Profitability uses it instead of the price; leave empty to use the price.")
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" loading={loading} onClick={handleSubmit}>
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
